class CartRecommendations extends HTMLElement {
  constructor() {
    super();
    this.deferLoad = this.hasAttribute('data-defer-load');
    this.onDrawerEvent = this.onDrawerEvent.bind(this);
    this.layoutTimer = null;
    this.resizeObserver = null;
  }

  connectedCallback() {
    document.addEventListener('cartdrawer:content-updated', this.onDrawerEvent);

    if (this.deferLoad) {
      document.addEventListener('cartdrawer:opened', this.onDrawerEvent);
      return;
    }

    if (this.querySelector('.cart-recommendations__list li')) {
      this.bindLayout();
      this.scheduleLayout();
      return;
    }

    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);
  }

  disconnectedCallback() {
    document.removeEventListener('cartdrawer:opened', this.onDrawerEvent);
    document.removeEventListener('cartdrawer:content-updated', this.onDrawerEvent);
    clearTimeout(this.layoutTimer);
    this.resizeObserver?.disconnect();
  }

  onDrawerEvent() {
    if (!document.querySelector('cart-drawer details[open]')) return;

    if (this.dataset.url && !this.querySelector('.cart-recommendations__list li')) {
      this.loadRecommendations();
      return;
    }

    this.scheduleLayout();
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);
    this.loadRecommendations();
  }

  loadRecommendations() {
    if (!this.dataset.url || this.querySelector('.cart-recommendations__list li')) return;

    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('cart-recommendations');
        const hasItems = recommendations
          && recommendations.querySelector('.cart-recommendations__list li');

        if (hasItems) {
          this.innerHTML = recommendations.innerHTML;
          this.style.removeProperty('display');
          this.bindLayout();
          this.scheduleLayout(true);
          document.dispatchEvent(new CustomEvent('cartdrawer:main-scroll-sync'));
        } else {
          // Nothing left to recommend (e.g. a cart whose items already cover the
          // recommended products). Hide the whole block so the empty-list loading
          // shimmer doesn't spin forever.
          this.style.display = 'none';
        }
      })
      .catch((e) => {
        console.error(e);
        this.style.display = 'none';
      });
  }

  bindLayout() {
    const list = this.querySelector('.cart-recommendations__list');
    if (!list || this.resizeObserver) return;

    this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
    this.resizeObserver.observe(list);
  }

  scheduleLayout(force = false) {
    clearTimeout(this.layoutTimer);
    this.layoutTimer = setTimeout(() => {
      const run = () => this.equalizeDescriptions(force);
      if (document.fonts?.ready) {
        document.fonts.ready.then(() => requestAnimationFrame(run));
      } else {
        requestAnimationFrame(run);
      }
    }, force ? 0 : 80);
  }

  equalizeDescriptions(force = false) {
    const list = this.querySelector('.cart-recommendations__list');
    if (!list || !window.matchMedia('(max-width: 749px)').matches) return;

    const descriptions = [...list.querySelectorAll('.product-description')];
    if (descriptions.length < 2) return;

    const signature = descriptions.map((desc) => desc.textContent.trim()).join('|');
    if (!force && list.dataset.recSig === signature) return;

    let maxHeight = 0;
    descriptions.forEach((desc) => {
      maxHeight = Math.max(maxHeight, this.measureDescription(desc));
    });

    if (maxHeight <= 0) return;

    const blockHeight = `${Math.ceil(maxHeight)}px`;
    list.style.setProperty('--rec-desc-height', blockHeight);
    list.dataset.recSig = signature;

    descriptions.forEach((desc) => {
      desc.style.minHeight = blockHeight;
    });
  }

  measureDescription(desc) {
    const clone = desc.cloneNode(true);
    const width = desc.getBoundingClientRect().width;

    clone.style.cssText = [
      'position:absolute',
      'visibility:hidden',
      'pointer-events:none',
      'top:0',
      'left:0',
      'width:' + (width || desc.offsetWidth) + 'px',
      'min-height:0',
      'height:auto',
      'max-height:none',
    ].join(';');

    this.appendChild(clone);
    const height = clone.offsetHeight;
    clone.remove();
    return height;
  }
}

customElements.define('cart-recommendations', CartRecommendations);
